<template>
	<div
		class="cog-choice-image-container"
		:class="{ 'is-loading': !isLoaded && !option.svg }"
	>
		<c-generated-avatar v-if="option.svg" :svg="option.svg" />
		<img
			v-else
			ref="image"
			:srcset="!isSvgFileType ? getSrcSet : null"
			:src="getSrc"
			:alt="option.label || option.displayValue"
			:width="dimensions.width"
			:height="dimensions.height"
			:class="getClasses"
			loading="lazy"
			@load="handleLoad"
		/>
	</div>
</template>

<script lang="ts">
	import CGeneratedAvatar from './GeneratedAvatar.vue';
	import { computed, defineComponent, ref } from 'vue';

	export type Option = {
		svg?: string;
		thumbnailImage?: string;
		displayValue?: string;
		choiceThumbnailImage?: string;
		choiceImageDimensions?: { width: number; height: number };
		choiceFullSizeImage?: string;
		fullSizeImage?: string;
	};

	export default defineComponent({
		name: 'CChoiceImage',
		components: { CGeneratedAvatar },
		props: {
			option: { type: Object as () => Option, required: true },
			isPersonField: { type: Boolean, required: true },
			choiceImageSize: { type: Number, required: true },
			fontSize: { type: Number, required: false, default: 14 }
		},
		emits: ['dimensions'],
		setup(props: { option: Option; isPersonField: boolean; choiceImageSize: number; fontSize: number }, { emit }) {
			const image = ref<HTMLImageElement | null>(null);
			const observedDimensions = ref<{ width: number; height: number } | null>({ width: 1000, height: 750 });
			const isLoaded = ref(false);

			const handleLoad = () => {
				isLoaded.value = true;
				observedDimensions.value = { width: image.value?.naturalWidth, height: image.value?.naturalHeight };
				if (!props.option.choiceImageDimensions?.width)
					emit('dimensions', observedDimensions.value);
			};

			const isSvgFileType = computed(() => {
				const image = props.option.choiceFullSizeImage || props.option.fullSizeImage;
				return image?.split('ct=image%2F')[1]?.includes('svg') ?? false;
			});

			const srcSetSizes = computed(() => {
				if (props.choiceImageSize === 0)
					return ['s', 's', 's'];
				else if (props.choiceImageSize === 1)
					if (props.fontSize < 15)
						return ['s', 's', 'l'];
					else
						return ['s', 'l', 'l'];
				else if (props.choiceImageSize === 2 && props.fontSize < 13)
					return ['s', 's', 'l'];
				else
					return ['l', 'l', 'l'];
			});

			const getSrcSet = computed(() => {
				if (!props.option.choiceThumbnailImage && !props.option.thumbnailImage)
					return;
				const sizes = {
					s: props.option.choiceThumbnailImage || props.option.thumbnailImage,
					l: props.option.choiceFullSizeImage || props.option.fullSizeImage
				};
				const srcSet = [];
				srcSetSizes.value.forEach((s, i) => srcSet.push(sizes[s] + ` ${i + 1}x`));
				return srcSet.join(', ');
			});

			const getSrc = computed(() => {
				return props.option.choiceThumbnailImage || props.option.thumbnailImage || null;
			});

			const dimensions = computed(() => {
				if (props.option.choiceImageDimensions?.width && props.option.choiceImageDimensions?.height)
					return { width: props.option.choiceImageDimensions.width, height: props.option.choiceImageDimensions.height };
				else
					return props.isPersonField ? { width: 50, height: 50 } : { width: observedDimensions.value.width, height: observedDimensions.value.height };
			});

			const getClasses = computed(() => {
				return {
					'cog-choice-image': props.option.choiceThumbnailImage,
					'cog-lookup-image': !props.option.choiceThumbnailImage,
					'cog-avatar': props.isPersonField
				};
			});

			return {
				isSvgFileType,
				getSrcSet,
				getSrc,
				dimensions,
				getClasses,
				image,
				isLoaded,
				handleLoad
			};
		}
	});
</script>

<style lang="scss">
#{$specificity-base} {

	.cog-choice-image-container {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		justify-content: center;
		max-width: 100%;
		height: var(--image-size);
		min-height: 1.7em;

		&.is-loading img {
			@keyframes image-loading {

				0% {
					background-color: hsla(var(--background-hsl), .2);
				}

				100% {
					background-color: hsla(var(--background-hsl), .05);
				}
			}
			animation: image-loading 1s ease-in-out infinite alternate;
		}

		img {
			display: inline-block;
			width: var(--image-size);
			height: auto;
			max-height: var(--image-size);
			border-radius: var(--input__border-radius);
			overflow: hidden;
			object-fit: contain;

			@include styled {
				vertical-align: middle;
			}
		}
	}

	.cog-checkable--inline .cog-choice-image-container {
		width: auto;
	}

	@mixin tiny-field {

		.el-checkbox-group,
		.el-radio-group,
		.cog-checkable__item {
			max-width: 100%;
		}

		.el-radio:not(:last-child),
		.el-checkbox:not(:last-child) {
			padding-right: 0;
		}

		.el-checkbox__input,
		.el-radio__input {
			width: 70%;
		}

		.el-checkbox__label,
		.el-radio__label {
			flex-shrink: 2;
		}

		.cog-choice-image-container:not(.cog-select__prefix-image) {
			flex-shrink: 0;
			width: min(calc(100% - 25px), var(--image-size));

			img {
				min-width: 0;
			}
		}
	}

	.cog-form[data-width~='700'] {

		.cog-col--2,
		.cog-col--3 {

			.cog-checkable:not(.cog-checkable--card)[class*='cog-image-size--'],
			.cog-picker[class*='cog-image-size--'] {
				@include tiny-field;
			}
		}

		.cog-col--4,
		.cog-col--5 {

			.cog-checkable:not(.cog-checkable--card),
			.cog-picker {

				&[class*='cog-image-size--2'],
				&[class*='cog-image-size--3'],
				&[class*='cog-image-size--4'] {
					@include tiny-field;
				}
			}
		}

		.cog-col--6,
		.cog-col--7 {

			.cog-checkable:not(.cog-checkable--card),
			.cog-picker {

				&[class*='cog-image-size--3'],
				&[class*='cog-image-size--4'] {
					@include tiny-field;
				}
			}
		}
	}
}
</style>